'use client';

/**
 * SSR-safe browser-environment hooks for the marketing landing.
 *
 * Both are built on React's `useSyncExternalStore`, the purpose-built
 * primitive for subscribing a component to an external (browser) store:
 *   - getSnapshot      reads the live client value
 *   - getServerSnapshot returns the SSR default (no browser globals at render)
 *   - subscribe        wires a `change` listener so the value stays fresh
 *
 * This replaces the older useState + useEffect + setState(matchMedia.matches)
 * pattern, which trips `react-hooks/set-state-in-effect` (it writes state from
 * an effect) AND risks a hydration mismatch. useSyncExternalStore satisfies
 * both: render reads getServerSnapshot on the server / first client paint, then
 * React reconciles to the live value without a state write in an effect.
 *
 * Related: [[knowledge/ui-components]] [[rules/ui-quality]] (hydration safety)
 */

import { useCallback, useSyncExternalStore } from 'react';

/**
 * Subscribe to a CSS media query. SSR/first-paint returns `serverDefault`;
 * after hydration it reflects (and stays in sync with) the live match.
 *
 * @param query         a valid CSS media query string
 * @param serverDefault value to assume on the server / before hydration
 */
export function useMediaQuery(query: string, serverDefault = false): boolean {
  const subscribe = useCallback(
    (onStoreChange: () => void) => {
      const mql = window.matchMedia(query);
      mql.addEventListener('change', onStoreChange);
      return () => mql.removeEventListener('change', onStoreChange);
    },
    [query]
  );

  const getSnapshot = useCallback(() => window.matchMedia(query).matches, [query]);
  const getServerSnapshot = useCallback(() => serverDefault, [serverDefault]);

  return useSyncExternalStore(subscribe, getSnapshot, getServerSnapshot);
}

/**
 * True when the user has requested reduced motion. SSR default is `false`
 * (assume motion allowed) so the server markup matches the common case.
 */
export function useReducedMotion(): boolean {
  return useMediaQuery('(prefers-reduced-motion: reduce)', false);
}
